/**
 * Structured logging utility for Email Ferret.
 * Supports log levels and structured context.
 */

import { isStaticAsset } from '@/lib/utils/static-assets';

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

/**
 * Type for log context values.
 * Supports common log context value types including nested objects and arrays.
 *
 * The fallback member is intentionally `any` (not `unknown`): the logger's context
 * index signature must remain assignable from arbitrary structured values and class
 * instances (e.g. an `Error` passed as context). Narrowing to `unknown` would break
 * existing call sites across the codebase that rely on this permissive behavior.
 */
type LogContextValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Date
  | Error
  | LogContext
  | LogContextValue[]
  | Record<string, unknown> // Allow complex objects for flexibility
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | any; // Fallback for unknown types (will be serialized safely)

/**
 * Log context interface with common fields and extensible index signature.
 * Provides type safety for common fields while allowing additional properties.
 */
interface LogContext {
  userId?: string;
  requestId?: string;
  route?: string;
  path?: string;
  pathname?: string;
  url?: string;
  endpoint?: string;
  method?: string;
  userAgent?: string;
  ip?: string;
  [key: string]: LogContextValue | undefined | null; // Allow null for compatibility
}

/**
 * Check if a log message or context contains a static asset URL that should be filtered.
 *
 * Filters out logs for static asset requests (Next.js chunks, static files, etc.)
 * to reduce noise in Vercel logs and Sentry. This prevents logging every static
 * asset request which can clutter logs and increase costs.
 *
 * Checks:
 * - The log message itself for static asset URLs
 * - Common URL fields in context (url, path, pathname, route, endpoint)
 * - All string values in the context object (recursively, including nested objects/arrays)
 *
 * @param message - The log message
 * @param context - Optional log context object
 * @returns true if the log should be filtered (not logged)
 *
 * @example
 * ```typescript
 * shouldFilterLog('/_next/static/chunks/main.js'); // true
 * shouldFilterLog('Request processed', { url: '/favicon.ico' }); // true
 * shouldFilterLog('Request processed', { url: '/api/health' }); // false
 * ```
 */
function shouldFilterLog(message: string, context?: LogContext): boolean {
  // Check message for static asset URLs
  if (isStaticAsset(message)) {
    return true;
  }

  if (!context) {
    return false;
  }

  // Check context for static asset URLs in common fields (fast path)
  const urlFields: (keyof LogContext)[] = ['url', 'path', 'pathname', 'route', 'endpoint'];
  for (const field of urlFields) {
    const value = context[field];
    if (typeof value === 'string' && isStaticAsset(value)) {
      return true;
    }
  }

  // Recursively check all string values in the context object
  // This catches static asset URLs in nested objects, arrays, or unexpected fields
  function checkValue(value: unknown): boolean {
    if (typeof value === 'string') {
      return isStaticAsset(value);
    }
    if (Array.isArray(value)) {
      return value.some(checkValue);
    }
    if (value && typeof value === 'object') {
      return Object.values(value).some(checkValue);
    }
    return false;
  }

  // Check all values in context (including nested structures)
  return checkValue(context);
}

class Logger {
  private logLevel: LogLevel;
  private isProduction: boolean;

  constructor() {
    // Get log level from environment or config
    const envLogLevel = process.env.LOG_LEVEL?.toUpperCase() as LogLevel;
    const validLevels: LogLevel[] = ['DEBUG', 'INFO', 'WARN', 'ERROR'];
    this.logLevel = validLevels.includes(envLogLevel) ? envLogLevel : 'INFO';
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      DEBUG: 0,
      INFO: 1,
      WARN: 2,
      ERROR: 3,
    };
    return levels[level] >= levels[this.logLevel];
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    let contextStr = '';
    if (context) {
      try {
        // Safely stringify context, handling circular references and non-serializable values
        contextStr = ` ${JSON.stringify(context, (key, value) => {
          // Handle circular references and non-serializable values
          if (value === undefined) {
            return '[undefined]';
          }
          if (typeof value === 'function') {
            return '[Function]';
          }
          if (value instanceof Error) {
            return {
              name: value.name,
              message: value.message,
              stack: value.stack,
            };
          }
          // Check for circular references (basic check)
          if (typeof value === 'object' && value !== null) {
            try {
              JSON.stringify(value);
            } catch {
              return '[Circular or non-serializable]';
            }
          }
          return value;
        })}`;
      } catch (error) {
        // If JSON.stringify still fails, use a safe fallback
        contextStr = ` [Context serialization failed: ${error instanceof Error ? error.message : String(error)}]`;
      }
    }
    return `[${timestamp}] [${level}] ${message}${contextStr}`;
  }

  /**
   * Internal log method that handles filtering, formatting, and output.
   *
   * @param level - Log level (DEBUG, INFO, WARN, ERROR)
   * @param message - Log message
   * @param context - Optional structured context object
   * @param error - Optional error object
   */
  private log(level: LogLevel, message: string, context?: LogContext, error?: Error): void {
    if (!this.shouldLog(level)) return;

    // In unit tests, logger output is usually noise and makes CI logs hard to read.
    // Allow opt-in console logging for logger-specific tests by setting LOGGER_ENABLE_CONSOLE=true.
    const shouldWriteToConsole =
      process.env.NODE_ENV !== 'test' || process.env.LOGGER_ENABLE_CONSOLE === 'true';
    if (!shouldWriteToConsole) {
      return;
    }

    // Filter out logs for static asset requests to reduce noise in Vercel logs
    if (shouldFilterLog(message, context)) {
      return;
    }

    const formatted = this.formatMessage(level, message, context);
    const errorStack = error ? `\n${error.stack}` : '';

    switch (level) {
      case 'DEBUG':
        console.debug(formatted + errorStack);
        break;
      case 'INFO':
        console.info(formatted + errorStack);
        break;
      case 'WARN':
        console.warn(formatted + errorStack);
        break;
      case 'ERROR':
        console.error(formatted + errorStack);
        break;
    }
  }

  debug(message: string, context?: LogContext): void {
    this.log('DEBUG', message, context);
  }

  info(message: string, context?: LogContext): void {
    this.log('INFO', message, context);
  }

  warn(message: string, context?: LogContext, error?: Error): void {
    this.log('WARN', message, context, error);
  }

  error(message: string, context?: LogContext, error?: Error): void {
    this.log('ERROR', message, context, error);

    // Capture error in Sentry if available (skip during unit tests to avoid tearing down issues)
    if (error && process.env.NODE_ENV !== 'test') {
      try {
        // Safely prepare error - remove request/response properties that can cause issues
        let safeError = error;
        if (error && typeof error === 'object') {
          try {
            // Create a safe copy without problematic properties
            if ('request' in error || 'response' in error) {
              safeError = new Error(error.message || 'Unknown error');
              safeError.name = error.name || 'Error';
              if (error.stack) {
                safeError.stack = error.stack;
              }
            }
          } catch {
            // If error preparation fails, use original error
            safeError = error;
          }
        }

        // Use enhanced Sentry utilities for better context
        import('./sentry')
          .then(({ captureSentryException }) => {
            return captureSentryException(safeError, {
              userId: context?.userId,
              requestId: context?.requestId || crypto.randomUUID(),
              route: context?.route,
              method: context?.method || 'UNKNOWN',
              userAgent: context?.userAgent,
              ip: context?.ip,
              tags: {
                logLevel: 'ERROR',
                source: 'logger',
              },
              extra: {
                message,
                // Safely spread context, filtering out undefined values and non-serializable properties
                ...(context
                  ? Object.fromEntries(
                      Object.entries(context).filter(
                        ([_, value]) =>
                          value !== undefined &&
                          typeof value !== 'function' &&
                          !(value instanceof Request) &&
                          !(value instanceof Response)
                      )
                    )
                  : {}),
              },
            });
          })
          .catch((sentryError) => {
            // Silently fail if Sentry is not available, but log in development
            if (process.env.NODE_ENV === 'development') {
              console.warn('Failed to capture error in Sentry:', sentryError);
            }
          });
      } catch (importError) {
        // Silently fail if Sentry import fails
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to import Sentry:', importError);
        }
      }
    }
  }

  // Convenience methods with common context patterns
  withUser(userId: string) {
    return {
      debug: (message: string, context?: LogContext) => this.debug(message, { userId, ...context }),
      info: (message: string, context?: LogContext) => this.info(message, { userId, ...context }),
      warn: (message: string, context?: LogContext, error?: Error) =>
        this.warn(message, { userId, ...context }, error),
      error: (message: string, context?: LogContext, error?: Error) =>
        this.error(message, { userId, ...context }, error),
    };
  }

  withRequest(requestId: string) {
    return {
      debug: (message: string, context?: LogContext) =>
        this.debug(message, { requestId, ...context }),
      info: (message: string, context?: LogContext) =>
        this.info(message, { requestId, ...context }),
      warn: (message: string, context?: LogContext, error?: Error) =>
        this.warn(message, { requestId, ...context }, error),
      error: (message: string, context?: LogContext, error?: Error) =>
        this.error(message, { requestId, ...context }, error),
    };
  }
}

// Export singleton instance
export const logger = new Logger();

// Export types for use in other modules
export type { LogLevel, LogContext };
